import { Measure } from '@cbsi-cbscom/measure';
import { ImportType } from 'library/ts/model/enums/config/ImportType';
import { ImportVO } from 'library/ts/model/vo/ImportVO';
import { Metrics } from 'library/ts/utilities/measure/Metrics';
import { TagEvent } from 'tag/ts/tag/events/TagEvent';
import { TagEventVO } from 'tag/ts/tag/events/TagEventVO';
import { TagLoggerString } from 'tag/ts/tag/model/enums/TagLoggerString';
import { PlayerType } from 'tag/ts/tag/model/type/PlayerType';
import { AbstractAdapter } from 'tag/ts/tag/player/adapters/abstract/AbstractAdapter';
import { AviaFactory } from 'tag/ts/tag/player/adapters/avia/AviaFactory';
import { AviaPlayerWrapper } from 'tag/ts/tag/player/adapters/avia/AviaPlayerWrapper';
import { AviaExtensionsManager } from 'tag/ts/tag/player/adapters/avia/extensions/AviaExtensionsManager';
import { AviaPlayerOptionsManager } from 'tag/ts/tag/player/adapters/avia/resources/AviaPlayerOptionsManager';
export class AviaAdapter extends AbstractAdapter {
    #aviaFactory;
    #aviaPlayerWrapper;
    #internalPlayerOptionsManager;
    #playerContainer;
    #playerNumber = 0;
    constructor(argument) {
        super({
            ...argument,
            name: 'AviaAdapter'
        });
    }
    get player() {
        return this.#aviaPlayerWrapper?.API;
    }
    get #playerOptionsManager() {
        if (this.utils.Type.isUndefined(this.#internalPlayerOptionsManager)) {
            this.#internalPlayerOptionsManager = new AviaPlayerOptionsManager({ tag: this.tag });
        }
        return this.#internalPlayerOptionsManager;
    }
    get adaptedPlayerInstance() {
        return this.#aviaPlayerWrapper?.adaptedPlayerInstance;
    }
    get playerNumber() {
        return this.#playerNumber;
    }
    initialize = () => {
        return new Promise((resolve, reject) => {
            this.tagEventsHandler(this.#onTagEvent, [
                TagEvent.ON_CREATE_PLAYER_REQUEST,
                TagEvent.ON_DESTROY_PLAYER_REQUEST
            ]);
            const dependencies = () => {
                let imports = [];
                const config = this.tag?.model?.config?.tag?.Imports?.params;
                Object.keys(config.avia).forEach((app) => {
                    const scriptURL = config.avia[app].url.regular;
                    const scriptNamespace = config.avia[app].namespace;
                    const addImport = (scriptURL, scriptNamespace) => {
                        if (!this.utils.Type.isEmpty(scriptURL)
                            &&
                                !this.utils.Import.alreadyPresent(scriptURL, scriptNamespace, !this.utils.URL.valid(scriptURL))) {
                            imports.push(new ImportVO(ImportType.SCRIPT, this.utils.URL.addCacheToken(scriptURL, this.runtimeVO.debug.cacheBust), scriptNamespace, window));
                        }
                    };
                    switch (app) {
                        case 'player_ui':
                            if (this.params.SKIN.enabled === true && this?.tag?.view?.playerType === PlayerType.FULL) {
                                addImport(scriptURL, scriptNamespace);
                            }
                            break;
                        default:
                            addImport(scriptURL, scriptNamespace);
                    }
                });
                imports = this.tag.tagUtilities.processImports(imports);
                this?.logger?.log('initialize:', 'dependencies to load:', imports);
                return imports;
            };
            const imports = dependencies();
            this?.logger?.log('initialize', 'loadDependencies (avia)', 'imports:', imports);
            Measure.start(Metrics.aviaLoadTime);
            this.importDependencies(imports).then((data) => {
                this?.logger?.log('initialize:', 'dependencies loaded (avia):', data);
                this.utils.Delay.delay()
                    .then(() => {
                    Measure.end(Metrics.aviaLoadTime);
                    this.#generateDeviceCapabilities().then((data) => {
                        resolve({ msg: 'Done', success: true, result: data });
                    });
                });
            });
        });
    };
    #generateDeviceCapabilities = () => {
        return new Promise((resolve, reject) => {
            const executeDeviceCapabilitiesCheck = () => {
                if (this.utils.Type.isNullOrUndefined(window?.avia?.getDeviceCapabilities)) {
                    this?.logger?.warn('window?.avia?.getDeviceCapabilities() is unavailable');
                    return reject('window?.avia?.getDeviceCapabilities() is unavailable');
                }
                const extensionsManager = new AviaExtensionsManager({ tag: this?.tag });
                extensionsManager.loadCapabilitiesExtensions()
                    .then(window?.avia?.getDeviceCapabilities)
                    .then((capabilities) => {
                    this?.logger?.log('getDeviceCapabilities promise resolved', capabilities);
                    window.SmartTag.deviceCapabilities = this.utils.DeepMerge.deepmerge({}, capabilities);
                    this.utils.Delay.delay().then(() => resolve({ msg: 'Device capabilities detected.', result: window.SmartTag.deviceCapabilities }));
                }, (reason) => {
                    this?.logger?.warn('getDeviceCapabilities promise rejected', reason);
                    reject(reason);
                });
            };
            const checkDeviceCapabilitiesNamespace = () => {
                if (!this.utils.Type.isEmpty(window?.SmartTag?.deviceCapabilities)) {
                    resolve({ msg: 'Device capabilities result already exists.', result: window?.SmartTag?.deviceCapabilities });
                }
                else {
                    executeDeviceCapabilitiesCheck();
                }
            };
            checkDeviceCapabilitiesNamespace();
        });
    };
    getAutoplayCapabilities = () => {
        return new Promise((resolve) => {
            let tempContainer = this.utils.DOM.stringToElement('<div style="position: absolute; left: 0; top: 0; width: 50px; height: 50px; display: none;"></div>');
            document.body.appendChild(tempContainer);
            this.#playerOptionsManager.getPlayerOptions(tempContainer).then((data) => {
                const playerOptions = data.result;
                playerOptions.plugins = [];
                window.avia.createVideoPlayer(playerOptions).then((player) => {
                    player.muted = false;
                    player.volume = 0.8;
                    window.avia.getAutoplayCapabilities().then((autoplayInfo) => {
                        window.avia.removeVideoPlayer(player.id).then(() => {
                            this.utils.DOM.removeElement(tempContainer).then((data) => {
                                tempContainer = undefined;
                                resolve({ autoplayInfo: autoplayInfo });
                            });
                        });
                    });
                });
            });
        });
    };
    getDeviceCapabilities = () => {
        this?.logger?.log('getDeviceCapabilities');
        return new Promise((resolve, reject) => {
            if (this.utils.Type.isNullOrUndefined(window?.avia?.getDeviceCapabilities)) {
                this?.logger?.warn('getDeviceCapabilities', 'avia api not available');
                return reject('avia api not available');
            }
            window.avia?.getDeviceCapabilities()?.then((result) => {
                this?.logger?.log('getDeviceCapabilities', 'avia promise responded with', result);
                resolve(result);
            }, (reason) => {
                this?.logger?.warn('getDeviceCapabilities', 'avia promise rejected because', reason);
                reject(reason);
            });
        });
    };
    get requestPlayerStop() {
        return this.#aviaPlayerWrapper.requestPlayerStop;
    }
    get resourceConfig() {
        return this.#aviaFactory.getResourceConfig();
    }
    #destroyAviaPlayerWrapper = () => {
        this?.logger?.log('destroyAviaPlayerWrapper:', 'wrapper:', this.#aviaPlayerWrapper);
        return new Promise((resolve, reject) => {
            const resolvePromise = () => {
                this?.logger?.log('destroyAviaPlayerWrapper:', 'Done. Resolve promise');
                resolve({ msg: 'Avia player wrapper destroyed' });
            };
            switch (this.utils.Type.isEmpty(this.#aviaPlayerWrapper)) {
                case false:
                    this.#aviaPlayerWrapper.cleanUp().then((data) => {
                        this.#aviaPlayerWrapper = undefined;
                        resolvePromise();
                    });
                    break;
                case true:
                    this.#aviaPlayerWrapper = undefined;
                    resolvePromise();
                    break;
            }
        });
    };
    #executeCreatePlayerRequest = (playerContainer) => {
        if (!this.utils.Type.isElement(playerContainer)) {
            this?.logger?.warn(TagLoggerString.EXECUTE_PLAYER_CREATE_EVENT, 'player container is not a valid DOM element:', playerContainer);
            return;
        }
        if (this.utils.Type.isEmpty(this.#aviaFactory)) {
            this.#aviaFactory = new AviaFactory({ tag: this.tag });
        }
        this.#playerContainer = playerContainer;
        this?.logger?.log('executeCreatePlayerRequest:', 'player container:', this.#playerContainer);
        this.utils.Delay.delay().then(() => {
            this.#aviaFactory.destroyPlayer(this.#aviaPlayerWrapper).then((data) => {
                this.#destroyAviaPlayerWrapper().then((data) => {
                    this.#playerOptionsManager.getPlayerOptions(this.#playerContainer).then((data) => {
                        this.#aviaFactory.createPlayer(data.result).then((data) => {
                            this?.logger?.log(TagLoggerString.EXECUTE_PLAYER_CREATE_EVENT, 'avia player created:', data?.player?.id);
                            this.#aviaPlayerWrapper = new AviaPlayerWrapper({ tag: this.tag, aviaPlayerInstance: data?.player });
                            this.#playerNumber++;
                            this.dispatchTagEvent(new TagEventVO(TagEvent.ON_CREATE_PLAYER_SUCCESS, this.tag, {
                                playerNumber: this.#playerNumber,
                                adaptedPlayerInstance: this.#aviaPlayerWrapper.adaptedPlayerInstance,
                                player: this.player
                            }, this));
                        }).catch((error) => {
                            this?.logger?.warn(TagLoggerString.EXECUTE_PLAYER_CREATE_EVENT, error);
                            this.dispatchTagEvent(new TagEventVO(TagEvent.ON_CREATE_PLAYER_FAILURE, this.tag, undefined, this));
                        });
                    });
                });
            });
        });
    };
    #executeDestroyPlayerRequest = (callback) => {
        const informApplication = (success) => {
            if (!this.utils.Type.isBoolean(success))
                return;
            if (this.utils.Type.isFunction(callback)) {
                callback(success);
            }
            this.dispatchTagEvent(new TagEventVO((success === true ? TagEvent.ON_DESTROY_PLAYER_SUCCESS : TagEvent.ON_DESTROY_PLAYER_FAILURE), this.tag, { playerNumber: this.#playerNumber }, this));
        };
        const destroyAviaPlayerWrapper = () => {
            this.#destroyAviaPlayerWrapper().then((data) => {
                this?.logger?.log('executeDestroyPlayerRequest: destroyAviaPlayerWrapper: success');
                informApplication(true);
            });
        };
        switch (this.utils.Type.isEmpty(this.player)) {
            case false:
                this.#aviaFactory.destroyPlayer(this.#aviaPlayerWrapper).then((data) => {
                    this?.logger?.log('destroyPlayer success', 'this.adaptedPlayerInstance:', this.adaptedPlayerInstance);
                    destroyAviaPlayerWrapper();
                }).catch((error) => {
                    this?.logger?.warn('executeDestroyPlayerRequest failure:', error);
                    informApplication(false);
                });
                break;
            case true:
                this?.logger?.log('executeDestroyPlayerRequest:', 'player does not exist');
                destroyAviaPlayerWrapper();
                break;
        }
    };
    #onTagEvent = (event) => {
        this?.logger?.log(TagLoggerString.ON_TAG_EVENT, event?.type, event);
        switch (event?.type) {
            case TagEvent.ON_CREATE_PLAYER_REQUEST:
                this.#executeCreatePlayerRequest(event?.data?.playerContainer);
                break;
            case TagEvent.ON_DESTROY_PLAYER_REQUEST:
                this.#executeDestroyPlayerRequest(event?.data?.eventCallback);
                break;
        }
    };
}
